import React, { useState, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Loader from '../../common/Loader';
import { selectedScheme, emiProductFetch, emiSkuSubmit, calculateEMIData } from '../../../slices/eligibility/eligibilitySlice';
import ImageComponent from '../../common/Image';
import { toTitleCaseAdvanced } from '../../../utils/functions';

const EmiCalculation = () => {
    const dispatch = useDispatch();

    const [showModal, setShowModal] = useState(null);

    // Redux state
    const { userData } = useSelector(state => state.auth);
    // const {schemeData} = useSelector(state => state.eligibility);
    const [schemeData, setSchemeData] = useState([]);

    // const { dealerList } = useSelector(state => state.dashboard);

    const authData = userData?.response_data?.data ?? {};
    const userType = authData.userType ?? null;
    const isLoggedIn = Boolean(authData.userType);
    const loggedInDealerCode = authData.employeeID ?? null;
    const customerType = authData.customerType ?? 'NTB';
    const dealerList = userData?.response_data?.dealer_data ?? [];

    // Local state
    const [loading, setLoading] = useState(false);
    const [productData, setProductData] = useState([]);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableVariants, setAvailableVariants] = useState([]);
    // const [schemeData, setSchemeData] = useState([]);
    const [selectedVariantDetails, setSelectedVariantDetails] = useState(null);
    const [emiCalculation, setEmiCalculation] = useState(null);

    // Modal states
    const [showDealerModal, setShowDealerModal] = useState(false);
    const [showBrandModal, setShowBrandModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showVariantModal, setShowVariantModal] = useState(false);

    // Search states
    const [dealerSearchTerm, setDealerSearchTerm] = useState('');
    const [brandSearchTerm, setBrandSearchTerm] = useState('');
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [variantSearchTerm, setVariantSearchTerm] = useState('');

    // Temporary selection states
    const [tempSelectedDealer, setTempSelectedDealer] = useState('');
    const [tempSelectedBrand, setTempSelectedBrand] = useState('');
    const [tempSelectedCategory, setTempSelectedCategory] = useState('');
    const [tempSelectedVariant, setTempSelectedVariant] = useState('');

    // Processing fee configuration
    const PROCESSING_FEES = {
        ETB: 199, // Existing to Books
        NTB: 299  // New to Books
    };

    // Utility function for key press handling
    const handleKeyPress = (event, callback) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };

    // Filtered data based on search terms
    const filteredDealers = dealerList.filter((dealer) =>
        dealer.dealercode.toLowerCase().includes(dealerSearchTerm.toLowerCase()) ||
        dealer.dealername.toLowerCase().includes(dealerSearchTerm.toLowerCase())
    );

    const filteredBrands = availableBrands.filter((brand) =>
        brand.name.toLowerCase().includes(brandSearchTerm.toLowerCase())
    );

    const filteredCategories = availableCategories.filter((category) =>
        category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    const filteredVariants = availableVariants.filter((variant) =>
    variant.description?.toLowerCase().includes(variantSearchTerm.toLowerCase())
);

    // Validation Schema
    const validationSchema = Yup.object({
        dealerCode: Yup.string().when('showDealerField', {
            is: true,
            then: () => Yup.string().required('Please select a dealer'),
            otherwise: () => Yup.string()
        }),
        brand: Yup.string().required('Please select a brand'),
        category: Yup.string().required('Please select a category'),
        variant: Yup.string().required('Please select a variant'),
        productCost: Yup.number()
            .required('Please enter product Price')
            .min(1, 'Product Price must be greater than 0'),
        scheme: Yup.string().required('Please select a scheme')
    });

    // Formik setup
    const formik = useFormik({
        initialValues: {
            dealerCode: '',
            brand: '',
            category: '',
            variant: '',
            productCost: '',
            scheme: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            calculateEMI(values);
        }
    });

    // Determine if dealer field should be shown
    const showDealerField = useMemo(() => {
        return isLoggedIn && userType === 'employee';
    }, [isLoggedIn, userType]);

    // Set initial dealer code for dealer users
    useEffect(() => {
        if (isLoggedIn && userType === 'dealer' && loggedInDealerCode) {
            formik.setFieldValue('dealerCode', loggedInDealerCode);
            fetchProductData(loggedInDealerCode);
        }
    }, [isLoggedIn, userType, loggedInDealerCode]);

    // Updated EMI Calculation Logic to use backend response
    const calculateEMI = async (values) => {
        const selectedScheme = schemeData.find(scheme => scheme.scheme_id === values.scheme);
        const productCost = parseFloat(values.productCost);

        if (!selectedScheme || !productCost) {
            toast.error('Please ensure all fields are filled correctly');
            return;
        }

        // Prepare payload for backend API
        const payload = {
            tenure: selectedScheme.tenure,
            advance_emi_tenure: selectedScheme.advance_emi_tenure,
            roi: selectedScheme.roi,
            downpayment: selectedScheme.downpayment,
            product_cost: productCost
        };

        setLoading(true);
        try {
            // Call the backend API using Redux action
            const response = await dispatch(calculateEMIData(payload));

            // Handle response directly in component
            if (response?.payload?.response_code === 'S100') {
                const emiData = response?.payload?.response_data;

                if (emiData) {
                    // Combine backend calculated data with selected scheme data
                    const calculationResult = {
                        // Backend calculated values
                        total_tenure: emiData.total_tenure,
                        initial_instalments: emiData.initial_instalments,
                        net_tenure: emiData.net_tenure,
                        roi: emiData.roi,
                        asset_emi: emiData.asset_emi,
                        initial_instalment_amount: emiData.initial_instalment_amount,
                        processing_fee: emiData.processing_fee,
                        downpayment: emiData.downpayment,
                        final_disbursement_amount: emiData.final_disbursement_amount,

                        // From selected scheme data for display
                        scheme_name: selectedScheme.scheme_name,
                        scheme_type: selectedScheme.scheme_type,
                        scheme_id: selectedScheme.scheme_id,
                        category: selectedScheme.category,
                        brand: selectedScheme.brand,
                        variant: selectedScheme.variant || selectedVariantDetails?.variant || '',
                        model: selectedScheme.model || selectedVariantDetails?.model || '',

                        // Additional fields for display compatibility
                        productCost: productCost,
                        selectedScheme: selectedScheme.scheme_name,
                        schemeType: selectedScheme.scheme_type,

                        // Map backend response to existing display fields
                        totalTenure: emiData.total_tenure,
                        initialInstalments: emiData.initial_instalments,
                        netTenure: emiData.net_tenure,
                        rateOfInterest: emiData.roi,
                        assetEMI: emiData.asset_emi,
                        initialInstalmentAmount: emiData.initial_instalment_amount,
                        processingFee: emiData.processing_fee,
                        downPayment: emiData.downpayment,
                        finalDisbursementAmount: emiData.final_disbursement_amount,
                        interestPayableAtDisbursement: selectedScheme.interest_at_disbursement || 0
                    };

                    setEmiCalculation(calculationResult);
                    toast.success('EMI calculation completed successfully!');

                    // Scroll to results
                    setTimeout(() => {
                        const resultsSection = document.getElementById('emi-results');
                        if (resultsSection) {
                            resultsSection.scrollIntoView({ behavior: 'smooth' });
                        }
                    }, 100);
                } else {
                    toast.error('No EMI calculation data received');
                }
            } else {
                !response?.payload?.error &&
                toast.error(response?.payload?.response_string || 'Failed to calculate EMI');
            }
        } catch (error) {
            console.error('Error calculating EMI:', error);
            toast.error('An error occurred while calculating EMI');
        } finally {
            setLoading(false);
        }
    };

    // Update handleVariantChange - don't fetch schemes immediately
    const handleVariantChange = async (e) => {
        const selectedVariantId = e.target.value;
        formik.setFieldValue('variant', selectedVariantId);
        formik.setFieldValue('scheme', ''); // Reset scheme
        formik.setFieldValue('productCost', ''); // Reset product cost

        if (selectedVariantId) {
            // Find selected variant details
            const variant = availableVariants.find(v => v.sku_id === selectedVariantId);
            setSelectedVariantDetails(variant);

            // Set default product cost from variant
            if (variant && variant.sku_mop) {
                formik.setFieldValue('productCost', variant.sku_mop);
                // Fetch schemes with default product cost
                await fetchSchemesWithProductCost(selectedVariantId, variant, variant.sku_mop);
            }
        } else {
            setSelectedVariantDetails(null);
            //setSchemeData([]);
        }
    };

    // Add new function to handle product cost change
    const handleProductCostChange = async (e) => {
        const productCost = e.target.value;
        formik.handleChange(e); // Update formik value

        // Fetch schemes when product cost is entered and variant is selected
        if (productCost && formik.values.variant && selectedVariantDetails) {
            await fetchSchemesWithProductCost(formik.values.variant, selectedVariantDetails, productCost);
        }
    };

    // Updated fetch function
    const fetchSchemesWithProductCost = async (variantId, variant, productCost) => {
        const payload = {
            sku_id: variantId,
            dealer_code: formik.values.dealerCode?.split('-')[0]?.trim() || formik.values.dealerCode,
            sku_max_fin_amt: variant.sku_max_fin_amt,
            sku_min_fin_amt: variant.sku_min_fin_amt,
            category: variant.category,
            brand: formik.values.brand, // Add brand from formik values
            model: variant.model,
            variant: variant.variant || '',
            product_cost: productCost,
            // reference_id: "CDLOS000000000000438"
        };

        setLoading(true);
        try {
            const response = await dispatch(emiSkuSubmit(payload));

            // Handle response directly in component
            if (response?.payload?.response_code === 'S100') {
                // Set scheme data directly from response
                const schemes = response?.payload?.response_data || [];
                setSchemeData(schemes);
                toast.success('Schemes loaded successfully!');

                // Auto-scroll to scheme section when schemes are loaded
                setTimeout(() => {
                    const schemeSection = document.querySelector('[data-scheme-section]');
                    if (schemeSection) {
                        schemeSection.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            } else {
                setSchemeData([]);
                !response?.payload?.error &&
                toast.error(response?.payload?.response_string || 'Failed to fetch scheme data');
            }
        } catch (error) {
            console.error('Error fetching scheme data:', error);
            setSchemeData([]);
            toast.error('An error occurred while fetching scheme data');
        } finally {
            setLoading(false);
        }
    };

    // Fetch product data when dealer is selected
    const fetchProductData = async (dealer_Code) => {
        if (!dealer_Code) return;
        setLoading(true);

        try {
            const response = await dispatch(emiProductFetch({
                "dealer_code": dealer_Code,
                "type": "brand_details"
            })).unwrap();

            if (response?.response_code === 'S100') {
                // The new response format is directly an array
                const productData = response.response_data || [];
                setProductData(productData);

                // Extract unique brands from the new format
                const brands = [];
                productData.forEach(brandGroup => {
                    // Each brandGroup has: brands, pic, product array
                    const brandInfo = {
                        name: brandGroup.brands, // brand name as string
                        pic: brandGroup.pic,     // brand picture URL
                        products: brandGroup.product || [] // array of products for this brand
                    };

                    // Check if brand already exists
                    if (!brands.find(b => b.name === brandInfo.name)) {
                        brands.push(brandInfo);
                    }
                });

                setAvailableBrands(response.response_data.map(b => ({
                    ...b,
                    name: b.brand_name
                })));

            } else {
                !response?.payload?.error &&
                toast.error(response?.response_string || 'Failed to fetch product data');
            }
        } catch (error) {
            console.error('Error fetching product data:', error);
            toast.error('An error occurred while fetching product data');
        } finally {
            setLoading(false);
        }
    };

    // Handle dealer selection
    const handleDealerChange = (e) => {
        const selectedDealer = e.target.value;
        formik.setFieldValue('dealerCode', selectedDealer);

        // Reset dependent fields
        formik.setFieldValue('brand', '');
        formik.setFieldValue('category', '');
        formik.setFieldValue('variant', '');
        formik.setFieldValue('productCost', '');
        formik.setFieldValue('scheme', '');

        // Clear dependent data
        setAvailableBrands([]);
        setAvailableCategories([]);
        setAvailableVariants([]);
        setSchemeData([]); // ← Clear local scheme data
        setSelectedVariantDetails(null);
        setEmiCalculation(null);

        if (selectedDealer) {
            fetchProductData(selectedDealer);
        }
    };

    // Handle brand selection
    const handleBrandChange = (e) => {
        const selectedBrand = e.target.value;
        formik.setFieldValue('brand', selectedBrand);

        // Reset dependent fields
        formik.setFieldValue('category', '');
        formik.setFieldValue('variant', '');
        formik.setFieldValue('productCost', '');
        formik.setFieldValue('scheme', '');

        if (selectedBrand) {
            // Extract categories for selected brand from new format
            const categories = [];

            // Find the brand group in productData
            const brandGroup = productData.find(group => group.brands === selectedBrand);

            if (brandGroup) {
                // Loop through products in this brand group
                brandGroup.product?.forEach(product => {
                    // Loop through models in each product
                    product.models?.forEach(model => {
                        // Add unique categories
                        if (!categories.find(c => c.category_code === model.category_code)) {
                            categories.push({
                                name: model.category,
                                code: model.category_code
                            });
                        }
                    });
                });
            }

            setAvailableCategories(categories);
        } else {
            setAvailableCategories([]);
            setAvailableVariants([]);
        }

        // Reset dependent states
        setSchemeData([]); // ← Add this line here
        setSelectedVariantDetails(null);
        setEmiCalculation(null);
    };

    // Handle category selection
    // Handle category selection
    const handleCategoryChange = (e) => {
        const selectedCategory = e.target.value;
        formik.setFieldValue('category', selectedCategory);

        // Reset dependent fields
        formik.setFieldValue('variant', '');
        formik.setFieldValue('productCost', '');
        formik.setFieldValue('scheme', '');

        if (selectedCategory && formik.values.brand) {
            // Extract variants for selected brand and category from new format
            const variants = [];

            // Find the brand group in productData
            const brandGroup = productData.find(group => group.brands === formik.values.brand);

            if (brandGroup) {
                // Loop through products in this brand group
                brandGroup.product?.forEach(product => {
                    // Loop through models in each product
                    product.models?.forEach(model => {
                        // Add models that match the selected category
                        if (model.category_code === selectedCategory) {
                            variants.push(model);
                        }
                    });
                });
            }

            setAvailableVariants(variants);
        } else {
            setAvailableVariants([]);
        }

        // Reset dependent states
        setSchemeData([]); // ← Add this line here
        setSelectedVariantDetails(null);
        setEmiCalculation(null);
    };

    // Fetch scheme data
    /* const fetchSchemeData = async (variantId) => {
      setLoading(true);
      try {
        const schemeRequestData = {
          "scheme_id": "", // Empty for initial fetch
          "dealer_code": formik.values.dealerCode,
          "sku_id": variantId,
          "reference_id": "CDLOS000000000000438"
        };
        
        const response = await dispatch(selectedScheme(schemeRequestData)).unwrap();
        
        if (response?.response_code === 'S100') {
          setSchemeData(response.response_data || []);
        } else {
          toast.error(response?.response_string || 'Failed to fetch scheme data');
        }
      } catch (error) {
        console.error('Error fetching scheme data:', error);
        toast.error('An error occurred while fetching scheme data');
      } finally {
        setLoading(false);
      }
    }; */

    // Fetch scheme data with new API
    const fetchSchemeDataNew = async (payload) => {
        setLoading(true);
        try {
            const response = await dispatch(emiSkuSubmit(payload));

            if (response?.payload?.response_code === 'S100') {
                // Schemes will be available in Redux store via schemeData selector
                toast.success('Schemes loaded successfully!');
            } else {
                !response?.payload?.error &&
                toast.error(response?.payload?.response_string || 'Failed to fetch scheme data');
            }
        } catch (error) {
            console.error('Error fetching scheme data:', error);
            toast.error('An error occurred while fetching scheme data');
        } finally {
            setLoading(false);
        }
    };

    const handleSchemeChange = (e) => {
        const selectedSchemeId = e.target.value;
        formik.setFieldValue('scheme', selectedSchemeId);

        // Clear EMI calculation results when scheme changes
        setEmiCalculation(null);

        // Auto-scroll to EMI calculation form when scheme is selected
        if (selectedSchemeId) {
            setTimeout(() => {
                const emiFormSection = document.querySelector('[data-emi-form-section]');
                if (emiFormSection) {
                    emiFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    };

    // Modal selection handlers
    const handleDealerSelect = (dealer) => {
        setTempSelectedDealer(`${dealer.dealername} - ${dealer.dealercode}`);
    };

    const handleBrandSelect = (brand) => {
        setTempSelectedBrand(brand.name);
    };

    const handleCategorySelect = (category) => {
        setTempSelectedCategory(category.name);
    };

    const handleVariantSelect = (variant) => {
    setTempSelectedVariant(variant.description);
};

    // Modal proceed handlers
    const handleDealerProceed = () => {
        if (tempSelectedDealer) {
            const dealerCode = tempSelectedDealer.split(' - ').pop();
            formik.setFieldValue('dealerCode', dealerCode);
            handleDealerChange({ target: { value: dealerCode } });
        }
        setShowDealerModal(false);
        setDealerSearchTerm('');
    };

    const handleBrandProceed = () => {
        if (tempSelectedBrand) {
            formik.setFieldValue('brand', tempSelectedBrand);
            handleBrandChange({ target: { value: tempSelectedBrand } });

            // Find selected brand object from availableBrands
            const selectedBrandObj = availableBrands.find(b => b.name === tempSelectedBrand);
            const dealerCode = formik.values.dealerCode;
            const brandId = selectedBrandObj?.brand_id;

            if (dealerCode && brandId) {
                setLoading(true);
                dispatch(emiProductFetch({
                    dealer_code: dealerCode,
                    type: 'category',
                    brand_id: brandId
                })).unwrap()
                    .then(response => {
                        if (response?.response_code === 'S100') {
                            setAvailableCategories((response.response_data || []).map(c => ({
                                ...c,
                                name: c.category // add name property for compatibility
                            })));
                        } else {
                            setAvailableCategories([]);
                            toast.error(response?.response_string || 'Failed to fetch categories');
                        }
                    })
                    .catch(error => {
                        setAvailableCategories([]);
                        toast.error('An error occurred while fetching categories');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
        setShowBrandModal(false);
        setBrandSearchTerm('');
    };

    const handleCategoryProceed = () => {
        if (tempSelectedCategory) {
            const category = availableCategories.find(c => c.name === tempSelectedCategory);
            if (category) {
                formik.setFieldValue('category', category.category_code);
                setAvailableVariants([]);
                handleCategoryChange({ target: { value: category.category_code } });

                // Prepare payload for emiProductFetch
                const dealerCode = formik.values.dealerCode;
                // Find selected brand object from availableBrands
                const selectedBrandObj = availableBrands.find(b => b.name === formik.values.brand);
                const brandId = selectedBrandObj?.brand_id;
                const categoryCode = category.category_code;

                if (dealerCode && brandId && categoryCode) {
                    setLoading(true);
                    dispatch(emiProductFetch({
                        dealer_code: dealerCode,
                        type: 'brand_sku',
                        category_code: categoryCode,
                        brand_id: brandId
                    })).unwrap()
                        .then(response => {
                            if (response?.response_code === 'S100') {
                                // response.response_data should be array of variants
                                setAvailableVariants(response.response_data || []);
                            } else {
                                setAvailableVariants([]);
                                toast.error(response?.response_string || 'Failed to fetch variants');
                            }
                        })
                        .catch(error => {
                            setAvailableVariants([]);
                            toast.error('An error occurred while fetching variants');
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                }
            }
        }
        setShowCategoryModal(false);
        setCategorySearchTerm('');
    };

   const handleVariantProceed = () => {
    if (tempSelectedVariant) {
        const variant = availableVariants.find(v => v.description === tempSelectedVariant);
        if (variant) {
            formik.setFieldValue('variant', variant.sku_id);
            handleVariantChange({ target: { value: variant.sku_id } });
        }
    }
    setShowVariantModal(false);
    setVariantSearchTerm('');
};  

    return (
        <>
            <div className="container-fluid kyc-tab-dash-app height-scroll emi-scroll pt-2">
                <div className="row">
                    {loading && <Loader />}
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row">
                            {showDealerField && (
                                <div className="col-12 floating-bottom">
                                    <label className="form-label emi-label">Select Dealer</label>
                                    <div
                                        className={`input-underline bg-transparent emi-select-input d-flex align-items-center justify-content-between ${formik.touched.dealerCode && formik.errors.dealerCode ? 'is-invalid' : ''}`}
                                        onClick={() => setShowDealerModal(true)}
                                        onKeyDown={(e) => handleKeyPress(e, () => setShowDealerModal(true))}
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Select dealer"
                                    >
                                        <span className="emi-label input-calci-label">
                                            {formik.values.dealerCode
                                                ? dealerList?.find(d => d.dealercode === formik.values.dealerCode)?.dealername + ' - ' + formik.values.dealerCode
                                                : 'Select'
                                            }
                                        </span>
                                        <i className="icon-arrowdown-L1"></i>
                                    </div>
                                    {formik.touched.dealerCode && formik.errors.dealerCode && (
                                        <div className="invalid-feedback">{formik.errors.dealerCode}</div>
                                    )}
                                </div>
                            )}

                            <div className="col-6 floating-bottom">
                                <label className="form-label emi-label">Select Brand</label>
                                <div
                                    className={`input-underline bg-transparent emi-select-input d-flex align-items-center justify-content-between ${formik.touched.brand && formik.errors.brand ? 'is-invalid' : ''}`}
                                    onClick={() => availableBrands.length > 0 && setShowBrandModal(true)}
                                    onKeyDown={(e) => handleKeyPress(e, () => availableBrands.length > 0 && setShowBrandModal(true))}
                                    tabIndex={0}
                                    role="button"
                                    aria-label="Select brand"
                                >
                                    <span className="emi-label calci-long-label input-calci-label">
                                        {formik.values.brand
                                            ? toTitleCaseAdvanced(formik.values.brand)
                                            : availableBrands.length > 0 ? 'Select' : 'No brands available'
                                        }
                                    </span>
                                    <i className="icon-arrowdown-L1"></i>
                                </div>
                                {formik.touched.brand && formik.errors.brand && (
                                    <div className="invalid-feedback">{formik.errors.brand}</div>
                                )}
                            </div>


                            {/* Category Selection */}
                            <div className="col-6 floating-bottom">
                                <label className="form-label emi-label">Select Category</label>
                                <div
                                    className={`input-underline bg-transparent emi-select-input d-flex align-items-center justify-content-between ${formik.touched.category && formik.errors.category ? 'is-invalid' : ''}`}
                                    onClick={() => availableCategories.length > 0 && setShowCategoryModal(true)}
                                    onKeyDown={(e) => handleKeyPress(e, () => availableCategories.length > 0 && setShowCategoryModal(true))}
                                    tabIndex={0}
                                    role="button"
                                    aria-label="Select category"
                                >
                                    <span className="emi-label calci-long-label input-calci-label">
                                        {formik.values.category
                                            ? toTitleCaseAdvanced(availableCategories.find(c => c.category_code === formik.values.category)?.name || '')
                                            : availableCategories.length > 0 ? 'Select' : 'No categories available'
                                        }
                                    </span>
                                    <i className="icon-arrowdown-L1"></i>
                                </div>
                                {formik.touched.category && formik.errors.category && (
                                    <div className="invalid-feedback">{formik.errors.category}</div>
                                )}
                            </div>

                            {/* Variant Selection */}
                           <div className="col-6 floating-top">
    <label className="form-label emi-label">Select Variant</label>
    <div
        className={`input-underline bg-transparent emi-select-input d-flex align-items-center justify-content-between ${formik.touched.variant && formik.errors.variant ? 'is-invalid' : ''}`}
        onClick={() => availableVariants.length > 0 && setShowVariantModal(true)}
        onKeyDown={(e) => handleKeyPress(e, () => availableVariants.length > 0 && setShowVariantModal(true))}
        tabIndex={0}
        role="button"
        aria-label="Select variant"
    >
        <span className="emi-label calci-long-label input-calci-label">
            {formik.values.variant
                ? (() => {
                    const variant = availableVariants.find(v => v.sku_id === formik.values.variant);
                    return variant ? variant.description : '';
                })()
                : availableVariants.length > 0 ? 'Select' : 'No variants available'
            }
        </span>
        <i className="icon-arrowdown-L1"></i>
    </div>
    {formik.touched.variant && formik.errors.variant && (
        <div className="invalid-feedback">{formik.errors.variant}</div>
    )}
</div>

                            {/* Product Cost Field - New Addition */}
                            {selectedVariantDetails && (
                                <div className="col-6 floating-bottom">
                                    <label htmlFor="input" className="label form-label emi-label">Product Price</label>
                                    {/* <div className="input-container has-icon d-flex"> */}
                                    <div className="input-container d-flex">
                                        <span className="font-serif mx-0 align-content-center">₹</span>
                                        <input type="text" name="productCost" className={`text-end emi-calc-purchase input-underline ${formik.touched.productCost && formik.errors.productCost ? 'is-invalid' : ''}`} id="input" required value={formik.values.productCost}
                                            onChange={formik.handleChange}  // ← Change this line
                                            onBlur={handleProductCostChange}
                                            placeholder=""
                                            min={selectedVariantDetails.sku_min_fin_amt}
                                            max={selectedVariantDetails.sku_max_fin_amt} />
                                    </div>
                                    {/* <div className="underline"></div> */}
                                    {/* </div> */}
                                    {formik.touched.productCost && formik.errors.productCost && (
                                        <div className="invalid-feedback">{formik.errors.productCost}</div>
                                    )}
                                    <small className="text-muted fw-lighter">
                                        Range: <span className="font-serif mx-1">₹</span>{Number(selectedVariantDetails?.sku_min_fin_amt).toLocaleString('en-IN')} - <span className="font-serif mx-1">₹</span>{Number(selectedVariantDetails?.sku_max_fin_amt).toLocaleString('en-IN')}
                                    </small>
                                </div>
                            )}

                            {/* Scheme Selection with enhanced display */}
                            {/*schemeData.length > 0 && formik.values.productCost && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label">Select Scheme *</label>
                      <div className="row">
                        {schemeData.map((scheme) => (
                          <div key={scheme.scheme_id} className="col-md-6 mb-3">
                            <div className={`card ${formik.values.scheme === scheme.scheme_id ? 'border-primary bg-light' : ''}`}>
                              <div className="card-body p-3">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="scheme"
                                    id={`scheme_${scheme.scheme_id}`}
                                    value={scheme.scheme_id}
                                    checked={formik.values.scheme === scheme.scheme_id}
                                    onChange={formik.handleChange}
                                  />
                                  <label className="form-check-label w-100" htmlFor={`scheme_${scheme.scheme_id}`}>
                                    <div>
                                      <strong>{scheme.scheme_name}</strong>
                                    </div>
                                    <div className="text-muted small">
                                      Type: {scheme.scheme_type}
                                    </div>
                                    <div className="small">
                                      Tenure: {scheme.total_tenure || scheme.tenure} months
                                    </div>
                                    <div className="small">
                                      Rate of Interest: {scheme.rate_of_interest}% (Flat)
                                    </div>
                                    {scheme.initial_instalments > 0 && (
                                      <div className="small">
                                        Initial Installments: {scheme.initial_instalments}
                                      </div>
                                    )}
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {formik.touched.scheme && formik.errors.scheme && (
                        <div className="text-danger small">{formik.errors.scheme}</div>
                      )}
                    </div>
                  </div>
                )*/}

                            {/* Replace the existing Scheme Selection section with this */}

                            {/* Scheme Selection  */}
                            {schemeData && schemeData.length > 0 && formik.values.productCost && (
                                <div className="col-12" data-scheme-section>
                                    <label className="form-label mb-2">Select Scheme</label>
                                    <div className="row">
                                        {schemeData?.map((data, index) => (
                                            <div className="col-12 col-lg-6 col-md-6 mb-3" key={data?.scheme_id || index}>
                                                <div className={`${formik.values.scheme === data?.scheme_id ? 'border-primary bg-light' : ''}`}>
                                                    <div className="product-details px-3 py-3">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="schema-num">
                                                                {`${data?.tenure}/${data?.advance_emi_tenure || 0}`}
                                                            </div>
                                                            <div
                                                                className="lh-sm flex-grow-1 ms-3"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => {
                                                                    formik.setFieldValue('scheme', data?.scheme_id);
                                                                    setEmiCalculation(null); // Add this line to clear results
                                                                    // Auto-scroll to EMI calculation form when scheme is selected
                                                                    setTimeout(() => {
                                                                        const emiFormSection = document.querySelector('[data-emi-form-section]');
                                                                        if (emiFormSection) {
                                                                            emiFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                        }
                                                                    }, 100);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        formik.setFieldValue('scheme', data?.scheme_id);
                                                                        setEmiCalculation(null); // Add this line to clear results
                                                                        // Auto-scroll to EMI calculation form when scheme is selected
                                                                        setTimeout(() => {
                                                                            const emiFormSection = document.querySelector('[data-emi-form-section]');
                                                                            if (emiFormSection) {
                                                                                emiFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                            }
                                                                        }, 100);
                                                                    }
                                                                }}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <div className="form-check ps-0">
                                                                    <label className="form-check-label" htmlFor={`scheme_${data?.scheme_id}`}>
                                                                        <p className="fw-semibold pt-0">{data?.scheme_name}</p>
                                                                        <p className="text-secondary mb-0">
                                                                            EMI - {+data?.tenure - +data?.advance_emi_tenure} X <span className="font-serif mx-1">₹</span>{Number(data?.emi_amount || 0).toLocaleString('en-IN')}&nbsp;&nbsp;DP - <span className="font-serif mx-1">₹</span>{Number(data?.downpayment || 0).toLocaleString('en-IN')}
                                                                        </p>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <ImageComponent
                                                                className="d-none"
                                                                fileName="i-icon.svg"
                                                                alt="Scheme info icon"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => setShowModal(data)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        setShowModal(data);
                                                                    }
                                                                }}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="scheme"
                                                                id={`scheme_${data?.scheme_id}`}
                                                                value={data?.scheme_id}
                                                                checked={formik.values.scheme === data?.scheme_id}
                                                                onChange={handleSchemeChange} // Change this from formik.handleChange
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {formik.touched.scheme && formik.errors.scheme && (
                                        <div className="text-danger small">{formik.errors.scheme}</div>
                                    )}
                                </div>
                            )}

                            {showModal && (
                                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                                    <div className="modal-dialog modal-lg">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">
                                                    <i className="fas fa-info-circle me-2"></i>
                                                    Scheme Details - {showModal.scheme_name}
                                                </h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    onClick={() => setShowModal(null)}
                                                ></button>
                                            </div>
                                            <div className="modal-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <table className="table table-borderless">
                                                            <tbody>
                                                                <tr>
                                                                    <th scope="row">Scheme Name:</th>
                                                                    <td>{showModal.scheme_name}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">Scheme Type:</th>
                                                                    <td>{showModal.scheme_type}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">Tenure:</th>
                                                                    <td>{showModal.tenure} months</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">EMI Amount:</th>
                                                                    <td><span className="font-serif mx-1">₹</span>{Number(showModal.emi_amount).toLocaleString('en-IN')}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">Down Payment:</th>
                                                                    <td><span className="font-serif mx-1">₹</span>{Number(showModal.downpayment).toLocaleString('en-IN')}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <table className="table table-borderless">
                                                            <tbody>
                                                                <tr>
                                                                    <th scope="row">Advance EMI Tenure:</th>
                                                                    <td>{showModal.advance_emi_tenure} months</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">Net Tenure:</th>
                                                                    <td>{showModal.net_tenure} months</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">Rate of Interest:</th>
                                                                    <td>{showModal.roi}%</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">Interest at Disbursement:</th>
                                                                    <td><span className="font-serif mx-1">₹</span>{Number(showModal.interest_at_disbursement).toLocaleString('en-IN')}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th scope="row">Financing Range:</th>
                                                                    <td><span className="font-serif mx-1">₹</span>{Number(showModal.sku_min_fin_amt).toLocaleString('en-IN')} - <span className="font-serif mx-1">₹</span>{Number(showModal.sku_max_fin_amt).toLocaleString('en-IN')}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="modal-footer">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => setShowModal(null)}
                                                >
                                                    Close
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary calc-btn-primary"
                                                    onClick={() => {
                                                        formik.setFieldValue('scheme', showModal.scheme_id);
                                                        setEmiCalculation(null); // Add this line to clear results
                                                        setShowModal(null);
                                                        // Auto-scroll to EMI calculation form when scheme is selected
                                                        setTimeout(() => {
                                                            const emiFormSection = document.querySelector('[data-emi-form-section]');
                                                            if (emiFormSection) {
                                                                emiFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                            }
                                                        }, 100);
                                                    }}
                                                >
                                                    Select This Scheme
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Modal Backdrop */}
                            {showModal && (
                                <>
                                    {/*<div className="modal-backdrop fade show" onClick={() => setShowModal(null)}></div>*/}
                                    <div
                                        className="modal-backdrop fade show"
                                        role="button"
                                        tabIndex="0"
                                        onClick={() => setShowModal(null)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                setShowModal(null);
                                            }
                                        }}
                                    ></div>
                                </>
                            )}

                        </div>
                    </form>

                    {/* EMI Calculation Results as per BRD */}
                    {emiCalculation && (
                        <>
                            <div className="col-12 mb-3" id="emi-results">
                                {/* Disclaimer */}
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                <p className="text-justify text-secondary disclaimer"><span className='dark-blue fw-semibold'>Disclaimer:</span><br />This is a rough estimate of EMI and down payment details calculated without credit decisioning. These calculations are subject to change based on the credit score of the customer.</p>
                            </div>

                            <div className="col-12 mb-4">
                                <div className="d-flex align-items-center justify-content-between">
                                    <p className="Pname-i">EMI Calculation Results</p>
                                </div>
                            </div>

                            <div className="col-12 col-lg-12 col-md-12">
                                <p className="text-secondary d-flex justify-content-between custom-bg">Selected Scheme <span className="dark-blue fw-semibold">{emiCalculation.selectedScheme}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Scheme Type <span className="dark-blue fw-semibold">{emiCalculation.schemeType}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Product Price <span className="dark-blue fw-semibold"><span className="font-serif mx-1">₹</span>{Number(formik.values.productCost).toLocaleString('en-IN')}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Tenure <span className="dark-blue fw-semibold">{emiCalculation.totalTenure} months</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Initial Installments<span className="dark-blue fw-semibold">{emiCalculation.initialInstalments}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Net Tenure<span className="dark-blue fw-semibold">{emiCalculation.netTenure} months</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Rate of Interest <span className="dark-blue fw-semibold">{emiCalculation.rateOfInterest}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">EMI<span className="dark-blue fw-semibold"><span className="font-serif mx-1">₹</span>{Number(emiCalculation?.assetEMI).toLocaleString('en-IN')}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Initial Installment Amount<span className="dark-blue fw-semibold"><span className="font-serif mx-1">₹</span>{Number(emiCalculation?.initialInstalmentAmount).toLocaleString('en-IN')}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Interest Payable at Disbursement<span className="dark-blue fw-semibold"><span className="font-serif mx-1">₹</span>{Number(emiCalculation?.interestPayableAtDisbursement).toLocaleString('en-IN')}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg">Processing Fee<span className="dark-blue fw-semibold"><span className="font-serif mx-1">₹</span>{Number(emiCalculation?.processingFee).toLocaleString('en-IN')}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg fw-bold">Down Payment<span className="dark-blue fw-bold"><span className="font-serif mx-1 bg-purple-txt">₹</span>{Number(emiCalculation?.downPayment).toLocaleString('en-IN')}</span></p>

                                <p className="text-secondary d-flex justify-content-between custom-bg fw-bold d-none">Final Disbursement Amount<span className="dark-blue fw-bold"><span className="font-serif mx-1 bg-purple-txt">₹</span>{Number(emiCalculation?.finalDisbursementAmount).toLocaleString('en-IN')}</span></p>
                            </div>

                            {/* Summary Cards */}
                            <div className="col-md-4 mt-3">
                                <div className="card calc-summary-card">
                                    <div className="card-body text-center">
                                        <h6 className="dark-blue small fw-semibold">Monthly EMI</h6>
                                        <h1 className='fw-semibold'><span className="font-serif mx-1">₹</span>{Number(emiCalculation?.assetEMI).toLocaleString('en-IN')}</h1>
                                        <small>for {emiCalculation.netTenure} months</small>
                                        <hr />

                                        <h6 className="dark-blue small fw-semibold">Down Payment</h6>
                                        <h1 className='fw-semibold'><span className="font-serif mx-1">₹</span>{Number(emiCalculation?.downPayment).toLocaleString('en-IN')}</h1>
                                        <small>to be paid upfront</small>
                                        <hr className='d-none' />

                                        <h6 className="dark-blue small fw-semibold d-none">Disbursement</h6>
                                        <h1 className='fw-semibold d-none'><span className="font-serif mx-1">₹</span>{Number(emiCalculation?.finalDisbursementAmount).toLocaleString('en-IN')}</h1>
                                        <small className='d-none'>amount to be disbursed</small>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer with action buttons */}
                <div className="position-fixed bottom-0 start-0 w-100 bg-white sticky-bottom-1" style={{ zIndex: 1000 }}>
                    <div className="container-fluid">
                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary calc-btn-outline-primary"
                                onClick={() => {
                                    formik.resetForm();
                                    setEmiCalculation(null);
                                    setSelectedVariantDetails(null);
                                    setSchemeData([]);
                                    setAvailableBrands([]);
                                    setAvailableCategories([]);
                                    setAvailableVariants([]);
                                }}
                            >
                                <i className="fas fa-redo me-1"></i>
                                Reset
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary calc-btn-primary ms-2"
                                disabled={loading || !formik.values.scheme || !formik.values.productCost}
                                onClick={(e) => {
                                    e.preventDefault();
                                    formik.handleSubmit();
                                }}
                            >
                                <i className="fas fa-calculator me-1"></i>
                                {loading ? 'Calculating...' : 'Calculate EMI'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {emiCalculation && (
                <footer className="sticky-bottom-1 d-none">
                    <div className="container-fluid ">
                        <div className="row">
                            <div className="col-sm-12 ">
                                <div className="d-flex justify-content-end">
                                    <button
                                        className="btn btn-outline-primary calc-btn-outline-primary me-2 d-none"
                                        onClick={() => {
                                            const details = `EMI Calculation Details:
                        Scheme: ${emiCalculation.selectedScheme}
                        Product Price: ₹${Number(emiCalculation.productCost).toLocaleString('en-IN')}
                        Monthly EMI: ₹${Number(emiCalculation.assetEMI).toLocaleString('en-IN')}
                        Down Payment: ₹${Number(emiCalculation.downPayment).toLocaleString('en-IN')}
                        Disbursement: ₹${Number(emiCalculation.finalDisbursementAmount).toLocaleString('en-IN')}
                        Tenure: ${emiCalculation.netTenure} months
                        Rate of Interest: ${emiCalculation.rateOfInterest}%`;
                                            navigator.clipboard.writeText(details);
                                            toast.success('EMI details copied to clipboard!');
                                        }}
                                    >
                                        <i className="icon-Contentdocument-copy me-1"></i>
                                    </button>
                                    <button className="btn btn-primary calc-btn-primary" type="button" onClick={() => {
                                        setEmiCalculation(null);
                                        formik.resetForm();
                                        setSelectedVariantDetails(null);
                                    }}>Calculate Again</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
            )}

            {/* Dealer Selection Modal */}
            <div className={`modal fade ${showDealerModal ? 'show' : ''}`}
                id="SelectDealer"
                tabIndex="-1"
                aria-labelledby="SelectDealerLabel"
                aria-hidden={!showDealerModal}
                style={{ display: showDealerModal ? 'block' : 'none' }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        setShowDealerModal(false);
                    }
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowDealerModal(false);
                    }
                }}>
                <div className="modal-dialog bottom-modal">
                    <div className="modal-content blue">
                        <div className="modal-header">
                            <div className="top-draw"></div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between px-4 w-100">
                            <h6 className="mb-1" id="SelectDealerLabel">Select Dealer</h6>
                        </div>

                        <div className="modal-body">
                            <form className="col-12 col-lg-auto mb-3 mb-lg-0 me-lg-3 position-relative" role="search" onSubmit={(e) => e.preventDefault()}>
                                <input
                                    type="search"
                                    className="form-control input-underline"
                                    placeholder="Search dealer"
                                    aria-label="Search"
                                    value={dealerSearchTerm}
                                    onChange={(e) => setDealerSearchTerm(e.target.value)}
                                />
                                <div className="has-i-search hidden-search-box brand-search">
                                    <span className="icon-searchnormal fs-4 inside-search"></span>
                                </div>
                            </form>
                            <ul className="list-group list-group-flush">
                                {filteredDealers.length > 0 ? (
                                    filteredDealers.map((dealer, index) => (
                                        <li
                                            key={index}
                                            className={`list-group-item border-0 text-secondary fw-normal ${tempSelectedDealer === `${dealer.dealername} - ${dealer.dealercode}` ? 'dealer-selected' : ''}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleDealerSelect(dealer)}
                                            onKeyDown={(e) => handleKeyPress(e, () => handleDealerSelect(dealer))}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Select dealer ${dealer.dealercode} - ${dealer.dealername}`}
                                        >
                                            {dealer.dealercode} - {dealer.dealername}
                                            {tempSelectedDealer === `${dealer.dealername} - ${dealer.dealercode}` && (
                                                <i className="icon-check float-end text-primary"></i>
                                            )}
                                        </li>
                                    ))
                                ) : (
                                    <li className="list-group-item border-0 text-secondary fw-semibold">
                                        No dealers found
                                    </li>
                                )}
                            </ul>
                        </div>

                        <div className="modal-footer border-0">
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleDealerProceed()
                                    }
                                }}
                                onClick={handleDealerProceed}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Brand Selection Modal */}
            <div className={`modal fade ${showBrandModal ? 'show' : ''}`}
                id="SelectBrand"
                tabIndex="-1"
                aria-labelledby="SelectBrandLabel"
                aria-hidden={!showBrandModal}
                style={{ display: showBrandModal ? 'block' : 'none' }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        setShowBrandModal(false);
                    }
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowBrandModal(false);
                    }
                }}>
                <div className="modal-dialog bottom-modal">
                    <div className="modal-content blue">
                        <div className="modal-header">
                            <div className="top-draw"></div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between px-4 w-100">
                            <h6 className="mb-1" id="SelectBrandLabel">Select Brand</h6>
                        </div>

                        <div className="modal-body">
                            <form className="col-12 col-lg-auto mb-3 mb-lg-0 me-lg-3 position-relative" role="search" onSubmit={(e) => e.preventDefault()}>
                                <input
                                    type="search"
                                    className="form-control input-underline"
                                    placeholder="Search brand"
                                    aria-label="Search"
                                    value={brandSearchTerm}
                                    onChange={(e) => setBrandSearchTerm(e.target.value)}
                                />
                                <div className="has-i-search hidden-search-box brand-search">
                                    <span className="icon-searchnormal fs-4 inside-search"></span>
                                </div>
                            </form>
                            <ul className="list-group list-group-flush">
                                {filteredBrands.length > 0 ? (
                                    filteredBrands.map((brand, index) => (
                                        <li
                                            key={index}
                                            className={`list-group-item border-0 text-secondary fw-normal ${tempSelectedBrand === brand.name ? 'dealer-selected' : ''}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleBrandSelect(brand)}
                                            onKeyDown={(e) => handleKeyPress(e, () => handleBrandSelect(brand))}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Select brand ${brand.name}`}
                                        >
                                            {toTitleCaseAdvanced(brand.name)}
                                            {tempSelectedBrand === brand.name && (
                                                <i className="icon-check float-end text-primary"></i>
                                            )}
                                        </li>
                                    ))
                                ) : (
                                    <li className="list-group-item border-0 text-secondary fw-semibold">
                                        No brands found
                                    </li>
                                )}
                            </ul>
                        </div>

                        <div className="modal-footer border-0">
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={handleBrandProceed}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Category Selection Modal */}
            <div className={`modal fade ${showCategoryModal ? 'show' : ''}`}
                id="SelectCategory"
                tabIndex="-1"
                aria-labelledby="SelectCategoryLabel"
                aria-hidden={!showCategoryModal}
                style={{ display: showCategoryModal ? 'block' : 'none' }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        setShowCategoryModal(false);
                    }
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowCategoryModal(false);
                    }
                }}>
                <div className="modal-dialog bottom-modal">
                    <div className="modal-content blue">
                        <div className="modal-header">
                            <div className="top-draw"></div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between px-4 w-100">
                            <h6 className="mb-1" id="SelectCategoryLabel">Select Category</h6>
                        </div>

                        <div className="modal-body">
                            <form className="col-12 col-lg-auto mb-3 mb-lg-0 me-lg-3 position-relative" role="search" onSubmit={(e) => e.preventDefault()}>
                                <input
                                    type="search"
                                    className="form-control input-underline"
                                    placeholder="Search category"
                                    aria-label="Search"
                                    value={categorySearchTerm}
                                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                                />
                                <div className="has-i-search hidden-search-box brand-search">
                                    <span className="icon-searchnormal fs-4 inside-search"></span>
                                </div>
                            </form>
                            <ul className="list-group list-group-flush">
                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map((category, index) => (
                                        <li
                                            key={index}
                                            className={`list-group-item border-0 text-secondary fw-normal ${tempSelectedCategory === category.name ? 'dealer-selected' : ''}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleCategorySelect(category)}
                                            onKeyDown={(e) => handleKeyPress(e, () => handleCategorySelect(category))}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Select category ${category.name}`}
                                        >
                                            {toTitleCaseAdvanced(category.name)}
                                            {tempSelectedCategory === category.name && (
                                                <i className="icon-check float-end text-primary"></i>
                                            )}
                                        </li>
                                    ))
                                ) : (
                                    <li className="list-group-item border-0 text-secondary fw-semibold">
                                        No categories found
                                    </li>
                                )}
                            </ul>
                        </div>

                        <div className="modal-footer border-0">
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={handleCategoryProceed}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Variant Selection Modal */}
            <div className={`modal fade ${showVariantModal ? 'show' : ''}`}
    id="SelectVariant"
    tabIndex="-1"
    aria-labelledby="SelectVariantLabel"
    aria-hidden={!showVariantModal}
    style={{ display: showVariantModal ? 'block' : 'none' }}
    onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            setShowVariantModal(false);
        }
    }}
    onClick={(e) => {
        if (e.target === e.currentTarget) {
            setShowVariantModal(false);
        }
    }}>
    <div className="modal-dialog bottom-modal">
        <div className="modal-content blue">
            <div className="modal-header">
                <div className="top-draw"></div>
            </div>

            <div className="d-flex align-items-center justify-content-between px-4 w-100">
                <h6 className="mb-1" id="SelectVariantLabel">Select Variant</h6>
            </div>

            <div className="modal-body">
                <form className="col-12 col-lg-auto mb-3 mb-lg-0 me-lg-3 position-relative" role="search" onSubmit={(e) => e.preventDefault()}>
                    <input
                        type="search"
                        className="form-control input-underline"
                        placeholder="Search variant"
                        aria-label="Search"
                        value={variantSearchTerm}
                        onChange={(e) => setVariantSearchTerm(e.target.value)}
                    />
                    <div className="has-i-search hidden-search-box brand-search">
                        <span className="icon-searchnormal fs-4 inside-search"></span>
                    </div>
                </form>
                <ul className="list-group list-group-flush">
                    {filteredVariants.length > 0 ? (
                        filteredVariants.map((variant, index) => (
                            <li
                                key={index}
                                className={`list-group-item border-0 text-secondary fw-normal ${tempSelectedVariant === variant.description ? 'dealer-selected' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleVariantSelect(variant)}
                                onKeyDown={(e) => handleKeyPress(e, () => handleVariantSelect(variant))}
                                tabIndex={0}
                                role="button"
                                aria-label={`Select variant ${variant.description}`}
                            >
                                {variant.description}
                                {tempSelectedVariant === variant.description && (
                                    <i className="icon-check float-end text-primary"></i>
                                )}
                            </li>
                        ))
                    ) : (
                        <li className="list-group-item border-0 text-secondary fw-semibold">
                            No variants found
                        </li>
                    )}
                </ul>
            </div>

            <div className="modal-footer border-0">
                <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={handleVariantProceed}
                >
                    Apply
                </button>
            </div>
        </div>
    </div>
</div>

            {/* Modal Backdrops */}
            {showDealerModal && <div className="modal-backdrop fade show"></div>}
            {showBrandModal && <div className="modal-backdrop fade show"></div>}
            {showCategoryModal && <div className="modal-backdrop fade show"></div>}
            {showVariantModal && <div className="modal-backdrop fade show"></div>}
        </>
    )
}
export default EmiCalculation;